import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import mainContext from "../context/MainContext/mainContext";
import { Button, Input, Select, notification, Row, Col, Alert } from "antd";
import { MdDelete } from "react-icons/md";
import { IoArrowBack } from "react-icons/io5";
import BillingForm from "./BillingForm";
import InventoryList from "./InventoryList";
import GrandTotal from "./GrandTotal";
import TotalProductionCost from "./TotalProductionCost";
import { HiOutlineDownload } from "react-icons/hi";
import { CgMail } from "react-icons/cg";
import SuffixInvList from "./SuffixInvList";
import InvoiceBillingForm from "./InvoiceBillingForm";
import InventoryListCompleted from "./InventoryListCompleted";
import { useSelector } from "react-redux";
import SubBDFProductionCost from "./SubBDFProductionCost";
import { buildBillingInventoryRows } from "../../../../../utils/billingInvMatch";
import { isGujaratBilling } from "../../../../../utils/billingGstUtils";

const { Option } = Select;
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

/** Same default as BillingForm / GenerateSubInvoice — UI can show this without writing `invoiceRemark` to state until the user edits. */
const DEFAULT_INVOICE_REMARK =
  "Being the charges for Advertisement through display your ad on Outdoor Hoarding sites at";

function normBrandCampaignField(v) {
  if (v == null || v === "") return null;
  const s = String(v).trim().toUpperCase();
  return s === "" ? null : s;
}

const BillingDetails = () => {
  //states
  const {
    id,
    setId,
    fetchTotalBillingInvoices,
    totalInvoices,
    fetchTotalIsArchivedBillingInvoices,
    isArchivedbillingInvoices,
  } = useContext(mainContext);
  const [subTotal, setSubTotal] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [selectedInventories, setSelectedInventories] = useState([]);
  const [selectedSuffixInventories, setSelectedSuffixInventories] = useState(
    []
  );
  // In BillingDetails component, add these states
  const [manualPrintingCharge, setManualPrintingCharge] = useState(undefined);
  const [manualMountingCharge, setManualMountingCharge] = useState(undefined);

  // Add states for production charge selection
  const [isPrintingChargeSelected, setIsPrintingChargeSelected] =
    useState(true);
  const [isMountingChargeSelected, setIsMountingChargeSelected] =
    useState(true);
  const [log, setLog] = useState(null);
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  let userId = useSelector((state) => state.user._id);

  // Add a state to track if subTotal exceeds splitAmount
  const [exceedsSplitAmount, setExceedsSplitAmount] = useState(false);
  // Add a state to track the remaining amount
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [selectedSubBDFs, setSelectedSubBDFs] = useState([]);

  const [billingData, setBillingData] = useState({
    gstNumber: null,
    state: null,
    bdfType: null,
    stamp: null,
    roNumber: null,
    parentCompanyName: null,
    aliasPartyName: null,
    remark: null,
    invoiceRemark:
      "Being the charges for Advertisement through display your ad on Outdoor Hoarding sites at",
    invoiceSubRemark: "",
    roFile: null,
    brandName: null,
    taxType: null,
    agencyCommission: null,
    campaignName: null,
    campaignDuration: null,
    inventories: [],
    suffixInventories: [],
    HSN: "",
    refNo: "",
    refDate: null,
    billingDate: new Date().toISOString(),
    //taxes
    taxType: 0,
    IGST: 0,
    SGST: 0,
    CGST: 0,
    municipalTax: 0,
    others: 0,
    round: 0,
  });

  const [subBDFPrintingCharge, setSubBDFPrintingCharge] = useState(0);
  const [subBDFMountingCharge, setSubBDFMountingCharge] = useState(0);
  const [subBDFData, setSubBDFData] = useState(null);
  const [allSubBDFData, setAllSubBDFData] = useState([]);
  const [confirmLoading, setConfirmLoading] = useState(false);

  /** Snapshot brand/campaign from API when this billing loads — used to send only if edited on Confirm. */
  const brandCampaignBaselineRef = useRef({
    brandName: null,
    campaignName: null,
  });

  useEffect(() => {
    if (!billingData?.billingId) return;
    brandCampaignBaselineRef.current = {
      brandName: normBrandCampaignField(billingData.brandName),
      campaignName: normBrandCampaignField(billingData.campaignName),
    };
  }, [billingData?.billingId]);

  /** When no inventories are selected, allow Confirm (e.g. printing/mounting production cost only). */
  const allInventoriesHaveBillingPeriod = useMemo(() => {
    if (!selectedInventories || selectedInventories.length === 0) return true;

    const byId = new Map(
      (billingData?.inventories || []).map((inv) => [inv._id, inv])
    );

    return selectedInventories.every((sel) => {
      const inv = byId.get(sel._id);
      if (!inv) return true;
      return Boolean(inv.billingStartDate && inv.billingEndDate);
    });
  }, [billingData?.inventories, selectedInventories]);

  const partyGstNumber =
    billingData?.splitGSTNumber || billingData?.gstNumber || null;
  const hasGstNumber = Boolean(
    partyGstNumber != null && String(partyGstNumber).trim() !== "",
  );
  const isB2cBilling = !billingData?.isFOC && !hasGstNumber;

  /** Billing > due only blocks Confirm for selected lines. */
  const billingAmountExceedsDue = useMemo(() => {
    if (!selectedInventories || selectedInventories.length === 0) return false;

    const byId = new Map(
      (billingData?.inventories || []).map((inv) => [inv._id, inv])
    );

    return selectedInventories.some((sel) => {
      const inv = byId.get(sel._id);
      if (!inv) return false;
      const rawDue = inv?.apiDueAmount;
      if (rawDue === undefined || rawDue === null || rawDue === "") return false;
      const due = Number(rawDue);
      if (!Number.isFinite(due)) return false;
      const billing = Number(inv?.specialRate) || 0;
      return Math.round(billing) > Math.round(due);
    });
  }, [billingData?.inventories, selectedInventories]);

  // New state to track included sub BDF production costs
  const [includedSubBDFPrintingCharge, setIncludedSubBDFPrintingCharge] =
    useState(0);
  const [includedSubBDFMountingCharge, setIncludedSubBDFMountingCharge] =
    useState(0);

  // function calculateTotalRate() {
  //   let totalRate = selectedInventories.reduce(
  //     (acc, next) =>
  //       acc +
  //       (Number(next?.specialRate) || 0) +
  //       (Number(next?.mountingCharge) || 0) +
  //       (Number(next?.printingCharge) || 0) +
  //       (Number(next?.innovationCharge) || 0),
  //     0
  //   );

  //   if (selectedSuffixInventories.length > 0) {
  //     totalRate += selectedSuffixInventories.reduce(
  //       (acc, next) => acc + (Number(next?.specialRate) || 0),
  //       0
  //     );
  //   }

  //   setSubTotal(totalRate);
  // }

  /** Same rules as TotalProductionCost.getTotalCharges — line-level sums only when tab=2 or no invoices. */
  function getTotalChargesFromInventoryLines() {
    if (!selectedInventories?.length)
      return { printingCharge: 0, mountingCharge: 0 };
    if (tab === "2" || totalInvoices === 0) {
      return selectedInventories.reduce(
        (acc, inv) => {
          acc.printingCharge += Number(inv.printingCharge) || 0;
          acc.mountingCharge += Number(inv.mountingCharge) || 0;
          return acc;
        },
        { printingCharge: 0, mountingCharge: 0 }
      );
    }
    return { printingCharge: 0, mountingCharge: 0 };
  }

  /**
   * Matches TotalProductionCost: manual overrides; else base = due (if any) else line-calculated;
   * then add included Sub-BDF. Apply checkbox selection so Subtotal matches Total Production Cost.
   */
  function getProductionChargesForSubtotal() {
    const calculatedCharges = getTotalChargesFromInventoryLines();
    const duePrinting = Number(billingData?.duePrintingAmount) || 0;
    const dueMounting = Number(billingData?.dueMountingAmount) || 0;

    const isSplitBilling =
      billingData?.splitAmount !== null && billingData?.splitAmount !== undefined;

    // Split BDF rule: due values (even 0) are authoritative; do not fall back to
    // summed parent BDF line charges.
    const basePrinting = isSplitBilling
      ? duePrinting
      : duePrinting > 0
        ? duePrinting
        : calculatedCharges.printingCharge;
    const baseMounting = isSplitBilling
      ? dueMounting
      : dueMounting > 0
        ? dueMounting
        : calculatedCharges.mountingCharge;

    const printingCharge =
      manualPrintingCharge !== undefined
        ? manualPrintingCharge
        : basePrinting + includedSubBDFPrintingCharge;

    const mountingCharge =
      manualMountingCharge !== undefined
        ? manualMountingCharge
        : baseMounting + includedSubBDFMountingCharge;

    const selectedPrinting =
      isPrintingChargeSelected && Number.isFinite(Number(printingCharge))
        ? Number(printingCharge)
        : 0;
    const selectedMounting =
      isMountingChargeSelected && Number.isFinite(Number(mountingCharge))
        ? Number(mountingCharge)
        : 0;

    return { selectedPrinting, selectedMounting };
  }

  function calculateTotalRate() {
    const inventoryById = new Map(
      (billingData?.inventories || []).map((inv) => [inv._id, inv])
    );

    let totalRate = selectedInventories.reduce((acc, next) => {
      const row = inventoryById.get(next._id) || next;
      const amount = Number(row?.specialRate) || 0;
      return acc + amount + (Number(row?.innovationCharge) || 0);
    }, 0);

    if (selectedSuffixInventories.length > 0) {
      totalRate += selectedSuffixInventories.reduce(
        (acc, next) => acc + (Number(next?.specialRate) || 0),
        0
      );
    }

    const { selectedPrinting, selectedMounting } = getProductionChargesForSubtotal();
    totalRate += selectedPrinting + selectedMounting;

    setSubTotal(totalRate);
  }

  useEffect(() => {
    calculateTotalRate();
  }, [
    selectedInventories,
    selectedSuffixInventories,
    manualPrintingCharge,
    manualMountingCharge,
    includedSubBDFPrintingCharge,
    includedSubBDFMountingCharge,
    isPrintingChargeSelected,
    isMountingChargeSelected,
    billingData?.duePrintingAmount,
    billingData?.dueMountingAmount,
    billingData?.billedInvs,
    billingData?.inventories,
    tab,
    totalInvoices,
  ]);

  // Handle including or excluding sub BDF costs
  const handleIncludeSubBDFCostsChange = (printingCharge, mountingCharge) => {
    setIncludedSubBDFPrintingCharge(printingCharge);
    setIncludedSubBDFMountingCharge(mountingCharge);

    // Reset manual charges when toggling sub BDF costs to avoid confusion
    setManualPrintingCharge(undefined);
    setManualMountingCharge(undefined);
  };

  // Add an effect to check if subTotal exceeds splitAmount
  useEffect(() => {
    if (tab === "1" && billingData?.splitAmount) {
      const splitAmountValue = Number(
        billingData?.remainingSubTotal || billingData?.splitAmount
      );
      if (subTotal > splitAmountValue) {
        setExceedsSplitAmount(true);
        setRemainingAmount(splitAmountValue - subTotal);
      } else {
        setExceedsSplitAmount(false);
        setRemainingAmount(splitAmountValue - subTotal);
      }
    }
  }, [subTotal, billingData.splitAmount, tab]);

  function setGrandTotalFunc() {
    const total =
      billingData?.IGST +
      billingData?.SGST +
      billingData?.CGST +
      billingData?.municipalTax +
      billingData?.otherTaxes +
      billingData?.round +
      billingData?.agencyCommission;
    let grandTotalValue = (subTotal * total) / 100 + subTotal;
    console.log("🤡🤡", grandTotalValue);
    setGrandTotal(grandTotalValue);
  }
  //useEffects
  useEffect(() => {
    fetchBillingInfoByID();
    fetchSubBDFDataByID();
    fetchTotalBillingInvoices();
    // fetchTotalIsArchivedBillingInvoices();
  }, []);

  console.log("isArchived bills: ", isArchivedbillingInvoices);
  useEffect(() => {
    setGrandTotalFunc();
  }, [billingData, subTotal]);

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // const handleOpenModal = async () => {
  //   setIsModalOpen(true);
  // };

  // const handleCloseModal = () => setIsModalOpen(false);

  //functions
  const fetchBillingInfoByID = async () => {
    try {
      if (id) {
        console.log("ID is", id);
        let response = await fetch(
          `${apiBaseUrl}/inventoryManagement/getBillingInfoById?id=${id}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const responseData = await response.json();

        if (response.ok) {
          const billingInfo = responseData.billingInfo.salesOrderId;
          const bdfId = billingInfo?.bdfId || {};
          const brandId = bdfId?.brandId || {};
          const secondLastLog = responseData.secondLastLog || null;
          const resolvedGstNumber =
            responseData?.billingInfo?.partyCompany?.gstNumber ||
            bdfId?.parentCompanyId?.gstNumber ||
            bdfId?.brandId?.partyCompany?.gstNumber ||
            null;
          const resolvedState =
            responseData?.billingInfo?.partyCompany?.state ||
            bdfId?.parentCompanyId?.state ||
            bdfId?.brandId?.partyCompany?.state ||
            null;
          const billedInvs = responseData.billingInfo.inventories;
          const billedSuffixInvs = responseData?.billingInfo?.suffixInventories;

          const orderTotalAmount = 
            (bdfId?.inventories?.reduce(
              (acc, inventory) =>
                acc +
                (inventory?.specialRate || 0) +
                (inventory?.printingCharge || 0) +
                (inventory?.mountingCharge || 0),
              0
            ) || 0) +
            (bdfId?.suffixInventories?.reduce(
              (acc, inventory) => acc + (inventory?.specialRate || 0),
              0
            ) || 0);
          
          const isFOC = (responseData?.billingInfo?.subTotal !== undefined && responseData?.billingInfo?.subTotal !== null ? responseData.billingInfo.subTotal : orderTotalAmount) === 0;

          console.log("😿🟢🟢", billingInfo);
          setBillingData({
            isFOC,
            billingId: id,
            salesOrderId: billingInfo?._id,
            bdfId: bdfId?._id,
            salesPerson: bdfId?.salesPerson,
            bdfStamp: bdfId?.stamp,
            gstNumber: resolvedGstNumber,
            state: resolvedState,
            stamp: billingInfo.stamp || null,
            roNumber: billingInfo.roNumber || null,
            parentCompanyName:  bdfId?.parentCompanyId?.name || bdfId?.parentCompanyName || bdfId?.brandId?.partyCompany?.name  || null,
            splitParentCompanyName:
              responseData?.billingInfo?.partyCompany?.name || null,
            splitGSTNumber:
              responseData?.billingInfo?.partyCompany?.gstNumber || null,
            splitState:
              responseData?.billingInfo?.partyCompany?.state || null,
            splitAmount: responseData?.billingInfo?.subTotal || null,
            remainingSubTotal:
              responseData?.billingInfo?.remainingSubTotal || null,
            remark: bdfId?.remark || null,
            roFile: bdfId?.roFile || null,
            brandName: brandId?.brandName || null,
            agencyCommission: billingInfo?.agencyCommission || 0,
            campaignName: bdfId?.campaignName || null,
            campaignDuration: bdfId?.campaignDuration || null,
            bdfType: bdfId?.bdfType,
            printingCharge: billingInfo?.printingCharge || 0,
            mountingCharge: billingInfo?.mountingCharge || 0,
            duePrintingAmount:
              responseData?.billingInfo?.duePrintingAmount || 0,
            dueMountingAmount:
              responseData?.billingInfo?.dueMountingAmount || 0,
            billedInvs: billedInvs,
            billedSuffixInvs: billedSuffixInvs,
            inventories: buildBillingInventoryRows(
              bdfId?.inventories,
              billedInvs,
            ),
            suffixInventories:
              bdfId?.suffixInventories?.map((inv) => ({
                _id: inv?._id,
                specialRate: inv?.specialRate || 0,
                purchaseRate: inv?.purchaseRate || 0,
                suffixCode: inv?.suffixCode || null,
                suffixInfo: inv?.suffixInfo || null,
                vendor: inv?.vendor || null,
                startDate: inv?.startDate || null,
                endDate: inv?.endDate || null,
              })) || [],
            //taxes
            taxType: billingInfo?.taxType || 0,
            municipalTax: billingInfo?.municipalTax || 0,
            otherTaxes: billingInfo?.otherTaxes || 0,
            round: billingInfo?.round || 0,
            isFromSub: Boolean(billingInfo?.isFromSub),
            bdfDate: bdfId?.bdfDate || null,
            billingDate: new Date().toISOString(),
            IGST: 0,
            SGST: 0,
            CGST: 0,
          });

          // let totalRate = bdfId?.inventories?.reduce((acc, next) => {
          //   return (
          //     acc +
          //     (Number(next?.specialRate) || 0) +
          //     (Number(next?.mountingCharge) || 0) +
          //     (Number(next?.printingCharge) || 0) +
          //     (Number(next?.innovationCharge) || 0)
          //   ); // Ensures specialRate is a number, defaults to 0 if undefined
          // }, 0);

          // if (bdfId?.suffixInventories?.length > 0) {
          //   totalRate += bdfId.suffixInventories.reduce(
          //     (acc, next) => acc + (Number(next?.specialRate) || 0),
          //     0
          //   );
          // }

          // setSubTotal(totalRate);

          if (secondLastLog) {
            setLog({
              parentCompanyName: secondLastLog?.parentCompanyId?.name || secondLastLog?.parentCompanyName || secondLastLog?.brandId?.partyCompany?.name || null,
              gstNumber:
                secondLastLog?.parentCompanyId?.gstNumber ||
                secondLastLog?.brandId?.partyCompany?.gstNumber ||
                null,
              remark: secondLastLog?.remark || null,
              brandName: secondLastLog?.brandId?.brandName || null,
              campaignName: secondLastLog?.campaignName || null,
              campaignDuration: secondLastLog?.campaignDuration || null,
              bdfType: secondLastLog?.bdfType || null,
              inventories:
                secondLastLog?.inventories?.map((inv) => ({
                  _id: inv?.invId?._id || false,
                  locationName: inv?.invId?.locations?.name || null,
                  CodeNo: inv?.invId?.CodeNo || null,
                  suffix: inv?.invId?.suffix || "HO",
                  maintenanceRate: inv?.maintenanceRate || null,
                  isTrading: inv?.invId?.isTrading || false,
                  ownedBy: inv?.invId?.ownedBy || false,
                  rpm: inv?.rpm || 0,
                  printingCharge: inv?.printingCharge || 0,
                  mountingCharge: inv?.mountingCharge || 0,
                  specialRate: inv?.specialRate || 0,
                  specialRatePerMonth: inv?.specialRatePerMonth || 0,
                  startDate: inv?.startDate || null,
                  endDate: inv?.endDate || null,
                })) || [],
              suffixInventories:
                secondLastLog?.inventories?.map((inv) => ({
                  _id: inv?._id,
                  specialRate: inv?.specialRate || 0,
                  purchaseRate: inv?.purchaseRate || 0,
                  suffixCode: inv?.suffixCode || null,
                  suffixInfo: inv?.suffixInfo || null,
                  vendor: inv?.vendor || null,
                  startDate: inv?.startDate || null,
                  endDate: inv?.endDate || null,
                })) || [],
            });
          }

          return responseData;
        } else {
          notification.error({
            message: responseData?.message || "Failed to fetch the sales order",
          });
        }
      }
    } catch (error) {
      notification.error({
        message: error.message,
      });
    }
  };

  const fetchSubBDFDataByID = async () => {
    try {
      if (id) {
        let response = await fetch(
          `${apiBaseUrl}/inventoryManagement/getSubBDFDataInBilling?billingId=${id}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const responseData = await response.json();

        if (
          responseData.success &&
          responseData.data &&
          Array.isArray(responseData.data)
        ) {
          // Store all subBDF data
          setAllSubBDFData(responseData.data);

          // If we have data, set the first item as default
          if (responseData.data.length > 0) {
            // Keep the existing subBDFData state for backward compatibility
            setSubBDFData(responseData.data[0].relatedBilling);

            // Set initial values to zero since selection will update them
            setSubBDFPrintingCharge(0);
            setSubBDFMountingCharge(0);
          } else {
            // Reset if no data
            setSubBDFData(null);
            setSubBDFPrintingCharge(0);
            setSubBDFMountingCharge(0);
          }
        } else {
          // If no data or error, reset all values
          setAllSubBDFData([]);
          setSubBDFData(null);
          setSubBDFPrintingCharge(0);
          setSubBDFMountingCharge(0);
        }
      }
    } catch (error) {
      console.error("Error fetching SubBDF data:", error);
      // Reset values on error
      setAllSubBDFData([]);
      setSubBDFData(null);
      setSubBDFPrintingCharge(0);
      setSubBDFMountingCharge(0);
    }
  };

  console.log("subBDFPrintingCharge", subBDFPrintingCharge);
  console.log("subBDFMountingCharge", subBDFMountingCharge);
  console.log("subBDFData", subBDFData);

  const handleFormChange = (fieldName, value) => {
    console.log(`Updating ${fieldName} with value:`, value);

    if (fieldName == "taxType") {
      setBillingData((prevData) => ({
        ...prevData,
        [fieldName]: value,
        SGST: value.toFixed(2) / 2,
        CGST: value.toFixed(2) / 2,
        IGST: 0,
      }));
      return;
    }
    if (fieldName == "agencyCommission" || fieldName == "campaignDuration") {
      value = String(value).replace(/\D/g, "");
      setBillingData((prevData) => ({
        ...prevData,
        [fieldName]: Number(value),
      }));
      return;
    }
    // if (fieldName == "printingCharge") {
    //   value = String(value).replace(/\D/g, "");
    //   let total = billingData.inventories.reduce((acc, next) => {
    //     return acc + (Number(next?.specialRate) || 0); // Ensures specialRate is a number, defaults to 0 if undefined
    //   }, 0);
    //   total += Number(value) + Number(billingData?.mountingCharge);
    //   setSubTotal(total);
    //   setBillingData((prevData) => ({
    //     ...prevData,
    //     [fieldName]: Number(value),
    //   }));
    //   return;
    // }

    // if (fieldName == "mountingCharge") {
    //   value = String(value).replace(/\D/g, "");
    //   let total = billingData.inventories.reduce((acc, next) => {
    //     return acc + (Number(next?.specialRate) || 0);
    //   }, 0);
    //   total += Number(value) + Number(billingData?.printingCharge);
    //   setSubTotal(total);
    //   setBillingData((prevData) => ({
    //     ...prevData,
    //     [fieldName]: Number(value),
    //   }));
    //   return;
    // }
    if (fieldName == "IGST") {
      setBillingData((prevData) => {
        if (!isGujaratBilling(prevData)) {
          return {
            ...prevData,
            [fieldName]: value,
            SGST: 0,
            CGST: 0,
            taxType: 0,
          };
        }
        return { ...prevData, [fieldName]: value, taxType: 0 };
      });
      return;
    }
    if (
      fieldName == "municipalTax" ||
      fieldName == "round" ||
      fieldName == "otherTaxes"
    ) {
      value = String(value).replace(/\D/g, "");
      setBillingData((prevData) => ({
        ...prevData,
        [fieldName]: Number(value),
      }));
      return;
    }

    // Handle refDate specifically since it's a date object from DatePicker
    if (fieldName === "refDate") {
      setBillingData((prevData) => ({
        ...prevData,
        [fieldName]: value ? value.toISOString() : null,
      }));
      return;
    }

    // Handle billingDate (Billing Date) from DatePicker
    if (fieldName === "billingDate") {
      setBillingData((prevData) => ({
        ...prevData,
        [fieldName]: value ? value.toISOString() : null,
      }));
      return;
    }

    // Handle refNo field
    if (fieldName === "refNo") {
      setBillingData((prevData) => ({
        ...prevData,
        [fieldName]: value || "",
      }));
      return;
    }

    if (fieldName === "brandName" || fieldName === "campaignName") {
      setBillingData((prevData) => ({
        ...prevData,
        [fieldName]: normBrandCampaignField(value),
      }));
      return;
    }

    setBillingData((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));
  };

  const handleInvChange = (value, id, field) => {
    let inv = billingData.inventories;
    const index = inv.findIndex((item) => item._id === id);
    const updatedInventories = [...inv];
    updatedInventories[index] = {
      ...updatedInventories[index],
      [field]: value,
    };

    setBillingData((prev) => ({
      ...prev,
      inventories: updatedInventories,
    }));

    const total = updatedInventories?.reduce((acc, next) => {
      return acc + (Number(next?.specialRate) || 0);
    }, 0);

    setSubTotal(total);
    setGrandTotalFunc();
  };

  const handleSalesOrderSubmission = async () => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/editBilling`,
        {
          method: "POST",
          credentials: "include", // Use "include" to send cookies with the request
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ...billingData }),
        }
      );

      // Check if response is okay, throw an error if not
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      if (responseData?.message) {
        notification.success({ message: responseData?.message });
        setId(null); //  This my friends kindly act as navigating to the previous page;
      }
    } catch (err) {
      console.error("Error:", err.message);
    }
  };

  const getFinalProductionCharges = () => {
    // Mirror what the UI shows (TotalProductionCost + subtotal rules).
    const calculatedCharges = getTotalChargesFromInventoryLines();
    const dueP = Number(billingData?.duePrintingAmount) || 0;
    const dueM = Number(billingData?.dueMountingAmount) || 0;
    const isSplitBilling =
      billingData?.splitAmount !== null && billingData?.splitAmount !== undefined;

    const basePrinting = isSplitBilling
      ? dueP
      : dueP > 0
        ? dueP
        : calculatedCharges.printingCharge;
    const baseMounting = isSplitBilling
      ? dueM
      : dueM > 0
        ? dueM
        : calculatedCharges.mountingCharge;

    const printingShown =
      manualPrintingCharge !== undefined
        ? manualPrintingCharge
        : basePrinting + includedSubBDFPrintingCharge;
    const mountingShown =
      manualMountingCharge !== undefined
        ? manualMountingCharge
        : baseMounting + includedSubBDFMountingCharge;

    const printingCharge = isPrintingChargeSelected
      ? Number(printingShown) || 0
      : 0;
    const mountingCharge = isMountingChargeSelected
      ? Number(mountingShown) || 0
      : 0;

    return { printingCharge, mountingCharge };
  };

  const handleConfirmAsFOC = async () => {
    setConfirmLoading(true);
    try {
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/proceedBillAsFOC`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ billingId: id }),
        }
      );
      if (response.ok) {
        notification.success({
          message: "Proceeded as FOC successfully",
        });
        setId(null);
      } else {
        notification.error({
          message: "Failed to proceed as FOC",
        });
      }
    } catch (error) {
      notification.error({ message: "An error occurred" });
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleConfirm = async () => {
    setConfirmLoading(true);
    try {
      // ✅ Extract only selected inventories with billing details
      const selectedInventoriesData = selectedInventories.map((inv) => {
        const row = {
          invId: inv?.inventoryId ?? inv?._id,
          startDate: inv?.billingStartDate || inv?.startDate,
          endDate: inv?.billingEndDate || inv?.endDate,
          amount: inv?.specialRate || 0,
        };
        // Billing sub-document _id (unique per line when same invId appears multiple times).
        // Backend must apply amount to this line, not the first matching invId only.
        if (inv?._id && !String(inv._id).startsWith("billing-row-")) {
          row.billingInventoryId = inv._id;
        }
        return row;
      });

      // ✅ Extract only selected suffix inventories
      const selectedSuffixInventoriesData = selectedSuffixInventories.map(
        (inv) => ({
          invId: inv?._id,
          startDate: inv?.startDate, // Ensure Billing Period is used
          endDate: inv?.endDate,
          amount: inv?.specialRate || 0, // Use updated Billing Amount
        })
      );

      // ✅ Get production charges
      const { printingCharge, mountingCharge } = getFinalProductionCharges();
      let finalHsnCode = billingData?.HSN?.split("-")[0]?.trim() || 998361;

      // ✅ Prepare API payload
      const payload = {
        salesOrderId: billingData.salesOrderId,
        inventories:
          selectedInventoriesData.length > 0
            ? selectedInventoriesData
            : undefined,
        suffixInventories:
          selectedSuffixInventoriesData.length > 0
            ? selectedSuffixInventoriesData
            : undefined,
        createdBy: userId,
        printingCharge,
        mountingCharge,
        isPrintingChargeIncluded: isPrintingChargeSelected,
        isMountingChargeIncluded: isMountingChargeSelected,
        IGST: billingData?.IGST,
        SGST: billingData?.SGST,
        CGST: billingData?.CGST,
        aliasPartyName: billingData?.aliasPartyName?.trim() || null,
        HSN: parseInt(finalHsnCode),
        id: id,
        subBDFIds: selectedSubBDFs.length > 0 ? selectedSubBDFs : undefined,
      };

      if (billingData?.splitAmount) {
        payload.subTotal = subTotal;
      }

      // Add refNo and refDate to payload only if they have values
      if (billingData?.refNo) {
        payload.refNo = billingData.refNo;
      }

      if (billingData?.refDate) {
        payload.refDate = billingData.refDate;
      }

      // Preserve explicit empty string — do not fall back to DEFAULT when user cleared the field.
      const remarkText =
        billingData?.invoiceRemark != null
          ? String(billingData.invoiceRemark).trim()
          : DEFAULT_INVOICE_REMARK;
      payload.remark = remarkText;
      if (billingData?.invoiceSubRemark?.trim()) {
        payload.subRemark = billingData.invoiceSubRemark.trim();
      }

      payload.createdAt = billingData?.billingDate || new Date().toISOString();

      if (isB2cBilling) {
        payload.isB2C = true;
      }

      const base = brandCampaignBaselineRef.current;
      const curBrand = normBrandCampaignField(billingData?.brandName);
      const curCamp = normBrandCampaignField(billingData?.campaignName);
      if (base.brandName !== curBrand) {
        payload.brandName = curBrand;
      }
      if (base.campaignName !== curCamp) {
        payload.campaignName = curCamp;
      }

      // console.log("📤 Sending data to API:", payload);

      // ✅ Call the API
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/createBillingInvoice`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      // ✅ Handle response
      const responseData = await response.json();
      if (response.ok) {
        setId(null);
        notification.success({ message: "Bill created successfully!" });
      } else {
        throw new Error(responseData.message || "Failed to create bill.");
      }
    } catch (error) {
      console.error("Error creating billing:", error);
      notification.error({ message: error.message });
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={{ display: "inline-flex", alignItems: "center" }}>
          <IoArrowBack
            className="sales-order-arrow-back-icon"
            onClick={() => setId(null)}
          />
          <div style={{ marginLeft: "1rem" }}>Details</div>
        </div>
      </div>
      {tab === "1" && (
        <BillingForm
          billingData={billingData}
          handleFormChange={handleFormChange}
          log={log}
        />
      )}
      {tab === "2" && (
        <InvoiceBillingForm invoiceData={billingData} tab={tab} />
      )}

      {tab === "1" && (
        <InventoryList
          billingData={billingData}
          handleInvChange={handleInvChange}
          setBillingData={setBillingData}
          handleFormChange={handleFormChange}
          log={log}
          handleSelectionChange={setSelectedInventories}
        />
      )}

      {/* Alert section for split amount tracking */}
      {tab === "1" && billingData?.splitAmount && (
        <div style={{ marginTop: "20px", marginBottom: "20px" }}>
          <Alert
            message={`Available Split Amount: ${formatCurrency(
              Number(billingData?.remainingSubTotal || billingData?.splitAmount)
            )}`}
            description={
              <div>
                <p>Current Sub Total: {formatCurrency(subTotal)}</p>
                <p>
                  Remaining: {formatCurrency(remainingAmount)}{" "}
                  {remainingAmount < 0 ? "(Exceeded)" : ""}
                </p>
              </div>
            }
            type={
              exceedsSplitAmount
                ? "error"
                : remainingAmount === 0
                ? "success"
                : "info"
            }
            showIcon
            style={{ marginBottom: "10px" }}
          />

          {exceedsSplitAmount && (
            <Alert
              message="Warning: Amount Exceeded"
              description={`Your total billing amount exceeds the split amount by ${formatCurrency(
                Math.abs(remainingAmount)
              )}. Please adjust your billing amount to proceed.`}
              type="warning"
              showIcon
            />
          )}

          {remainingAmount > 0 &&
            remainingAmount <
              Number(
                billingData?.remainingSubTotal || billingData?.splitAmount
              ) *
                0.1 && (
              <Alert
                message="Almost Complete"
                description={`You are close to utilizing the full split amount. ${formatCurrency(
                  remainingAmount
                )} remaining.`}
                type="success"
                showIcon
              />
            )}
        </div>
      )}

      {tab === "2" && (
        <InventoryListCompleted
          billingData={billingData}
          handleSelectionChange={setSelectedInventories}
        />
      )}

      {billingData?.suffixInventories &&
        billingData?.suffixInventories?.length > 0 && (
          <SuffixInvList
            billingData={billingData}
            handleSuffixSelectionChange={setSelectedSuffixInventories}
          />
        )}

      {subBDFData && tab === "1" && (
        <SubBDFProductionCost
          allSubBDFData={allSubBDFData}
          onIncludeSubBDFCostsChange={handleIncludeSubBDFCostsChange}
          onSubBDFSelectionChange={setSelectedSubBDFs}
        />
      )}

      {tab !== "2" && (
        <div
          className="grandTotal-TotalProductionCost-wrapper"
          style={{ marginTop: "1rem" }}
        >
          <Row>
            <Col span={12}>
              <GrandTotal
                billingData={billingData}
                subTotal={subTotal}
                handleFormChange={handleFormChange}
                grandTotal={grandTotal}
                setGrandTotal={setGrandTotal}
              />
            </Col>
            <Col span={12}>
              <TotalProductionCost
                selectedInventories={selectedInventories}
                totalInvoices={totalInvoices}
                manualPrintingCharge={manualPrintingCharge}
                manualMountingCharge={manualMountingCharge}
                onPrintingChargeChange={setManualPrintingCharge}
                onMountingChargeChange={setManualMountingCharge}
                includedSubBDFPrintingCharge={includedSubBDFPrintingCharge}
                includedSubBDFMountingCharge={includedSubBDFMountingCharge}
                isPrintingChargeSelected={isPrintingChargeSelected}
                isMountingChargeSelected={isMountingChargeSelected}
                onPrintingChargeSelectionChange={setIsPrintingChargeSelected}
                onMountingChargeSelectionChange={setIsMountingChargeSelected}
                duePrintingAmount={billingData?.duePrintingAmount || 0}
                dueMountingAmount={billingData?.dueMountingAmount || 0}
                isSplitBilling={
                  billingData?.splitAmount !== null &&
                  billingData?.splitAmount !== undefined
                }
              />
              {billingData?.isFromSub && billingData?.bdfDate && (
                <div
                  style={{
                    marginTop: "0.75rem",
                    marginLeft: "4rem",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 12px",
                    background: "#fafafa",
                    border: "1px solid lightgrey",
                    borderRadius: "4px",
                  }}
                >
                  <span style={{ color: "rgba(0, 0, 0, 0.65)" }}>
                    Sub BDF Date:
                  </span>
                  <span style={{ fontWeight: 600 }}>
                    {billingData.bdfDate}
                  </span>
                </div>
              )}
            </Col>
          </Row>
        </div>
      )}

      {tab === "1" && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="primary"
            onClick={billingData?.isFOC ? handleConfirmAsFOC : handleConfirm}
            loading={confirmLoading}
            disabled={
              confirmLoading ||
              (!billingData?.isFOC && (exceedsSplitAmount && billingData.splitAmount)) ||
              (!billingData?.isFOC && !allInventoriesHaveBillingPeriod) ||
              (!billingData?.isFOC && billingAmountExceedsDue)
            }
            style={{
              marginBottom: "2rem",
              ...(billingData?.isFOC
                ? { backgroundColor: "#52c41a", borderColor: "#52c41a" }
                : isB2cBilling
                  ? {
                      backgroundColor: "#fff7e6",
                      borderColor: "#ffd591",
                      color: "#d46b08",
                    }
                  : {}),
            }}
          >
            {confirmLoading
              ? "Processing..."
              : billingData?.isFOC
                ? "Confirm as FOC"
                : isB2cBilling
                  ? "Proceed as B2C"
                  : "Confirm"}
          </Button>
        </div>
      )}

      {/* <GenerateInvoice
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        billingData={billingInfo}
      /> */}
    </div>
  );
};

export default BillingDetails;
